import { createFileRoute, Navigate, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { workspaceSlug } from "../lib/hosts";
import { useMembers, useWorkspace } from "../lib/workspaces";
import { contributes } from "../../shared/schemas/workspaces";
import { idSchema } from "../../shared/schemas/issues";
import { useProject, useProjectByKey } from "../lib/projects";
import { prefetchIssue, useAssignToMe } from "../lib/issues";
import { useBacklog } from "../lib/sprints";
import { issueSearchSchema } from "../lib/issue-filters";
import { IssueFilters } from "../components/issues/IssueFilters";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { IssuePeek } from "../components/issues/IssuePeek";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { selectionPage, useIssueShortcuts, type QuickControl } from "../components/issues/use-issue-shortcuts";
import { useShowsSelection } from "../lib/in-view";
import { createIssueIn, openCommandBar } from "../lib/keyboard";
import { BacklogView } from "../components/backlog/BacklogView";
import { EpicsPanel } from "../components/backlog/EpicsPanel";
import { ProjectKeyMoved, ProjectUnreachable } from "../components/projects/ProjectStates";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { NoResults, Unavailable } from "../components/ui/states";
import { strings } from "../lib/strings";
import { fill } from "../lib/words";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";

const words = strings.backlog;

/** The backlog's address: the list's filters, the epic chosen in its panel, and the issue open in the peek. */
const backlogSearchSchema = issueSearchSchema.extend({
  epic: z
    .union([idSchema, z.literal("none")])
    .optional()
    .catch(undefined),
});

function BacklogPage() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/backlog" });
  const slug = workspaceSlug();
  const search = useSearch({ from: "/admin/p/$projectKey/backlog" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/backlog" });
  const view = useWorkspace(slug);
  const byKey = useProjectByKey(slug, projectKey);
  const members = useMembers(slug);
  const [focusedIssueId, setFocusedIssueId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quickControl, setQuickControl] = useState<QuickControl | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const project = byKey.project;
  const detail = useProject(project?.id ?? "");
  const contributor = view.data !== undefined && contributes(view.data.role) && detail.data?.canEdit === true;
  const backlog = useBacklog(project?.id ?? "", {
    ...(search.epic === undefined ? {} : { epic: search.epic }),
    ...(search.assignee === undefined ? {} : { assignee: search.assignee }),
    ...(search.type === undefined ? {} : { type: search.type }),
    ...(search.priority === undefined ? {} : { priority: search.priority }),
    ...(search.q === undefined ? {} : { q: search.q }),
  });
  const answer = backlog.data;
  const issues = answer === undefined ? [] : [...answer.sprints.flatMap((s) => s.issues), ...answer.backlog.issues];
  // A filter in force that finds nothing says so, with the way to clear it (BUILD_SPEC §4.6).
  const filtered = [search.epic, search.assignee, search.type, search.priority, search.q].some((v) => v !== undefined);
  const drawerIssue = search.issue ? issues.find((i) => i.key === search.issue) : undefined;
  const deleteWithUndo = useDeleteWithUndo(project?.id ?? "");
  const assign = useAssignToMe(project?.id ?? "");
  // The selection, for the command bar's changes to it (BUILD_SPEC §4.3), where the person may make them.
  useShowsSelection(contributor && project !== undefined ? project.id : null, [...selected], () =>
    setSelected(new Set()),
  );
  const openIssue = (key: string) => void navigate({ search: (prev) => ({ ...prev, issue: key }) });

  useIssueShortcuts({
    issues,
    peeking: drawerIssue !== undefined,
    onExpand: () =>
      drawerIssue !== undefined &&
      void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } }),
    focusedIssueId,
    setFocusedIssueId,
    onOpen: (issue) => openIssue(issue.key),
    // A new issue here, in the frame's dialog: the backlog has none of its own.
    onCreate: contributor && project !== undefined ? () => createIssueIn(project.id) : undefined,
    onFocusSearch: () => searchRef.current?.focus(),
    onQuick: (issue, control) => {
      // On selected issues the key changes them all, at the command bar's page for it (BUILD_SPEC §4.3).
      const page = contributor && selected.size > 0 ? selectionPage(control) : null;
      if (page !== null) openCommandBar(page);
      else {
        setQuickControl(control);
        openIssue(issue.key);
      }
    },
    onAssignMe: contributor
      ? (issue) => {
          if (selected.size === 0) assign.one(issue);
          else
            assign.many([...selected], (everyOne) => {
              if (everyOne) setSelected(new Set());
            });
        }
      : undefined,
    onSelect: (issue) => {
      const next = new Set(selected);
      if (next.has(issue.id)) next.delete(issue.id);
      else next.add(issue.id);
      setSelected(next);
    },
  });

  if (view.isLoading || byKey.isLoading || (project !== undefined && (detail.isLoading || backlog.isLoading))) {
    return (
      <div className="mx-auto max-w-6xl space-y-3 px-6 py-8">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }
  if ((view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 401)) || !view.data) {
    return <ToWorkspaces />;
  }
  if (!byKey.loaded)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Unavailable what={strings.projects.list.theProjects} onRetry={byKey.refetch} page />
      </div>
    );
  if (!project || project.joinable) return <ProjectUnreachable project={project} />;
  if (project.key !== projectKey) return <ProjectKeyMoved from={projectKey} to={project.key} />;
  const shownProject = detail.data?.project ?? project;
  // A Business project plans by dates, not a backlog (BUILD_SPEC §10.2): its work is on its board and list.
  if (shownProject.method === "plan") return <Navigate to="/admin/p/$projectKey" params={{ projectKey }} />;

  const memberName = (id: string | null) =>
    id ? ((members.data ?? []).find((m) => m.userId === id)?.userName ?? null) : null;
  const closeDrawer = () => {
    setQuickControl(null);
    void navigate({ search: (prev) => ({ ...prev, issue: undefined }) });
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6 flex flex-col gap-4">
        <ProjectHeading
          project={shownProject}
          projectKey={projectKey}
          current="backlog"
          canManage={detail.data?.canManage ?? false}
        />
        {answer?.method === "kanban" && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.kanbanBody}</p>
        )}
        <IssueFilters
          slug={slug}
          statuses={detail.data?.statuses ?? []}
          types={(detail.data?.types ?? []).filter((type) => type.level === 0)}
          showStatus={false}
          value={search}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }) })}
          searchInputRef={searchRef}
        />
      </header>

      {selected.size > 0 && (
        <div
          role="status"
          className="sticky top-0 z-10 mb-3 flex items-center gap-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] px-3 py-2 text-sm shadow-sm"
        >
          {fill(words.selected, { count: selected.size })}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            {words.clearSelection}
          </Button>
        </div>
      )}

      {answer === undefined ? (
        <Unavailable what={words.theBacklog} onRetry={() => void backlog.refetch()} />
      ) : filtered && issues.length === 0 ? (
        <NoResults
          onClear={() =>
            void navigate({
              search: (prev) => ({
                ...prev,
                epic: undefined,
                assignee: undefined,
                type: undefined,
                priority: undefined,
                q: undefined,
              }),
            })
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[14rem_1fr]">
          {answer.method === "scrum" && (
            <aside className="hidden lg:block">
              <EpicsPanel
                epics={answer.epics}
                chosen={search.epic}
                onChoose={(epic) => void navigate({ search: (prev) => ({ ...prev, epic }) })}
              />
            </aside>
          )}
          <div className={answer.method === "scrum" ? undefined : "lg:col-span-2"}>
            <BacklogView
              answer={answer}
              project={shownProject}
              statuses={detail.data?.statuses ?? []}
              canPlan={contributor}
              canManage={(detail.data?.canManage ?? false) && shownProject.archivedAt === null}
              memberName={memberName}
              selected={selected}
              setSelected={setSelected}
              focusedIssueId={focusedIssueId}
              setFocusedIssueId={setFocusedIssueId}
              onOpen={(issue) => openIssue(issue.key)}
              onPrefetch={(id) => {
                prefetchIssue(qc, id);
                void import("../components/issues/MarkdownView").catch(() => undefined);
              }}
            />
          </div>
        </div>
      )}

      {drawerIssue && (
        <IssuePeek
          issueId={drawerIssue.id}
          issueKey={drawerIssue.key}
          slug={slug}
          projectId={project.id}
          pageNeeds={720}
          onClose={closeDrawer}
          onExpand={() =>
            void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } })
          }
          onRequestDelete={() => deleteWithUndo.requestDelete(drawerIssue.id, { onStart: closeDrawer })}
          issueKeyForId={(id) => issues.find((i) => i.id === id)?.key ?? null}
          autoOpen={quickControl ?? undefined}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/backlog")({
  validateSearch: backlogSearchSchema,
  component: BacklogPage,
});
